import { ViewPlugin, Decoration, EditorView } from '@codemirror/view';
import { RangeSetBuilder } from '@codemirror/state';

/**
 * Claims Indentation Extension
 * Applies hanging indent styles based on claim indentation levels
 */

// Define CSS classes for different indentation levels
const claimIndentStyles = EditorView.theme({
    // Apply to ALL lines in claims editor - this ensures wrapped text stays indented
    '.cm-line': {
        paddingLeft: '3ch !important',
    },
    
    // Remove padding from comment lines so they align with claim numbers
    '.cm-line.cm-inline-comment': {
        paddingLeft: '0 !important',
    },
    
    // Main claims (1., 2., etc.) - pull the number back to the left
    '.cm-line-main-claim': {
        textIndent: '-3ch !important',
    },
    
    // Level 1 subclaims ((a), (b), etc.) - 7ch total indent
    '.cm-line-subclaim-1': {
        paddingLeft: '7ch !important',
        textIndent: '-7ch !important',
    },
    
    // Level 2 subclaims ((i), (ii), etc.) - 11ch total indent
    '.cm-line-subclaim-2': {
        paddingLeft: '11ch !important',
        textIndent: '-11ch !important',
    },
    
    // Vertical connection lines for subclaims
    '.cm-line-with-vertical-line-1': {
        position: 'relative',
        '&::before': {
            content: '""',
            position: 'absolute',
            left: '1.5ch', // Position line just before the subclaim marker
            top: '0',
            bottom: '0',
            width: '1px',
            backgroundColor: 'var(--color-border-muted, #e5e5e5)',
            zIndex: 1
        }
    },
    
    '.cm-line-with-vertical-line-2': {
        position: 'relative',
        '&::after': {
            content: '""',
            position: 'absolute',
            left: '6.5ch', // Position line just before the roman numeral marker
            top: '0',
            bottom: '0',
            width: '1px',
            backgroundColor: 'var(--color-border-muted, #e5e5e5)',
            zIndex: 1
        }
    }
});

// Patterns to detect different claim types
const MAIN_CLAIM_PATTERN = /^\s*\d+\.\s/;
const SUBCLAIM_1_PATTERN = /^\s{4}\([a-z]\)\s/;  // 4 spaces for lettered subclaims
const SUBCLAIM_2_PATTERN = /^\s{8}\([ivxlcdm]+\)\s/i;  // 8 spaces for roman subclaims

// Also match continuation lines with proper indents (when no autonumbering)
const INDENT_1_PATTERN = /^\s{7}(?!\()(\S|$)/;  // 7 spaces, not followed by (, then non-space OR end of line
const INDENT_2_PATTERN = /^\s{11}(?!\()(\S|$)/;  // 11 spaces, not followed by (, then non-space OR end of line

function detectClaimType(lineText) {
    if (MAIN_CLAIM_PATTERN.test(lineText)) {
        return 'main-claim';
    } else if (SUBCLAIM_2_PATTERN.test(lineText)) {
        return 'subclaim-2';
    } else if (SUBCLAIM_1_PATTERN.test(lineText)) {
        return 'subclaim-1';
    } else if (INDENT_2_PATTERN.test(lineText)) {
        // Line with 11-space indent (level 2 without numbering)
        return 'subclaim-2';
    } else if (INDENT_1_PATTERN.test(lineText)) {
        // Line with 7-space indent (level 1 without numbering)
        return 'subclaim-1';
    } else {
        // For continuation lines, don't apply any special styling
        return null;
    }
}

function analyzeClaimStructure(view) {
    const doc = view.state.doc;
    const structure = [];
    
    for (let i = 1; i <= doc.lines; i++) {
        const line = doc.line(i);
        const claimType = detectClaimType(line.text);
        if (claimType) {
            structure.push({
                lineNumber: i,
                type: claimType,
                line: line
            });
        }
    }
    
    return structure;
}


const claimIndentationPlugin = ViewPlugin.fromClass(class {
    constructor(view) {
        this.decorations = this.buildDecorations(view);
    }

    update(update) {
        if (update.docChanged || update.viewportChanged) {
            this.decorations = this.buildDecorations(update.view);
        }
    }

    buildDecorations(view) {
        const builder = new RangeSetBuilder();
        const claimStructure = analyzeClaimStructure(view);
        
        // Determine which lines need vertical lines by finding continuous groups
        const linesNeedingVerticalLines1 = new Set();
        const linesNeedingVerticalLines2 = new Set();
        
        
        // Find all subclaim-1 groups and their boundaries
        let i = 0;
        while (i < claimStructure.length) {
            if (claimStructure[i].type === 'subclaim-1') {
                // Found start of a subclaim-1 group
                const groupStart = i;
                let subclaim1Count = 0;
                
                // Count consecutive subclaim-1 and include any subclaim-2 that follow
                while (i < claimStructure.length && 
                       (claimStructure[i].type === 'subclaim-1' || claimStructure[i].type === 'subclaim-2')) {
                    if (claimStructure[i].type === 'subclaim-1') {
                        subclaim1Count++;
                    }
                    i++;
                }
                const groupEnd = i - 1;
                
                // Only add lines if there are 2+ subclaim-1 items
                if (subclaim1Count >= 2) {
                    const startLine = claimStructure[groupStart].lineNumber;
                    let endLine;
                    if (groupEnd < claimStructure.length - 1) {
                        endLine = claimStructure[groupEnd + 1].lineNumber - 1;
                    } else {
                        endLine = view.state.doc.lines;
                    }
                    
                    for (let lineNum = startLine; lineNum <= endLine; lineNum++) {
                        linesNeedingVerticalLines1.add(lineNum);
                    }
                }
            } else {
                i++;
            }
        }
        
        // Find all subclaim-2 groups independently 
        i = 0;
        while (i < claimStructure.length) {
            if (claimStructure[i].type === 'subclaim-2') {
                // Found start of a subclaim-2 group
                const groupStart = i;
                let subclaim2Count = 0;
                
                // Count consecutive subclaim-2 
                while (i < claimStructure.length && claimStructure[i].type === 'subclaim-2') {
                    subclaim2Count++;
                    i++;
                }
                const _groupEnd = i - 1;

                // Only add lines if there are 2+ subclaim-2 items
                if (subclaim2Count >= 2) {
                    const startLine = claimStructure[groupStart].lineNumber;
                    let endLine;
                    if (i < claimStructure.length) {
                        endLine = claimStructure[i].lineNumber - 1;
                    } else {
                        endLine = view.state.doc.lines;
                    }
                    
                    for (let lineNum = startLine; lineNum <= endLine; lineNum++) {
                        linesNeedingVerticalLines2.add(lineNum);
                    }
                }
            } else {
                i++;
            }
        }
        
        for (let { from, to } of view.visibleRanges) {
            for (let pos = from; pos <= to;) {
                const line = view.state.doc.lineAt(pos);
                const claimType = detectClaimType(line.text);
                const classes = [];
                
                // Add decoration for lines with claim numbers to apply negative text-indent
                if (claimType) {
                    classes.push(`cm-line-${claimType}`);
                }
                
                // Add vertical line classes
                if (linesNeedingVerticalLines1.has(line.number)) {
                    classes.push('cm-line-with-vertical-line-1');
                }
                if (linesNeedingVerticalLines2.has(line.number)) {
                    classes.push('cm-line-with-vertical-line-2');
                }
                
                // Apply decorations if any classes were added
                if (classes.length > 0) {
                    const decoration = Decoration.line({
                        class: classes.join(' ')
                    });
                    
                    builder.add(line.from, line.from, decoration);
                }
                
                pos = line.to + 1;
            }
        }
        
        return builder.finish();
    }
}, {
    decorations: v => v.decorations
});

export function createClaimIndentationExtension() {
    return [
        claimIndentationPlugin,
        claimIndentStyles
    ];
}